// Quick Estimate 3 — image preflight.
//
// Runs before we hand a File to agentsApi.evaluateImage. Two jobs:
//   1. Reject obviously-broken inputs early (wrong mime, gigantic file)
//      so the user gets a real error instead of a 30-second timeout.
//   2. Downscale to a max edge of 2048px before upload. The AI
//      doesn't need 12MP; smaller files mean faster evaluation and
//      fewer flaky uploads on weak service-bay LTE.
//
// Returns {ok, file, reason}. The component decides what UI to
// show — preflight stays headless.

const MAX_EDGE_PX = 2048
const HARD_MAX_BYTES = 25 * 1024 * 1024 // 25MB — block
const SOFT_MAX_BYTES = 10 * 1024 * 1024 // 10MB — warn (not blocking)
const JPEG_QUALITY = 0.86

export type PreflightReason =
  | "not-image"
  | "too-large"
  | "decode-failed"
  | "downscale-failed"

export interface PreflightOk {
  ok: true
  file: File
  /** True if downscale ran and produced a smaller file. */
  downscaled: boolean
  /** Original byte size — used for telemetry / "we shrunk this from 8MB" surface copy. */
  originalSize: number
  /** True when the original was over SOFT_MAX_BYTES. The component
   *  can surface a quiet "compressing…" hint while preflight runs. */
  wasSoftLarge: boolean
}

export interface PreflightFail {
  ok: false
  reason: PreflightReason
  /** Best-effort message — passed straight to the verdict overlay. */
  message: string
}

export type PreflightResult = PreflightOk | PreflightFail

export async function preflightImage(file: File): Promise<PreflightResult> {
  if (!file.type.startsWith("image/")) {
    return {
      ok: false,
      reason: "not-image",
      message: "That doesn't look like an image — try a JPEG or PNG.",
    }
  }
  if (file.size > HARD_MAX_BYTES) {
    return {
      ok: false,
      reason: "too-large",
      message: "Photo is too big to upload. Try a lower-resolution shot.",
    }
  }

  // Decode + downscale via canvas. createImageBitmap is faster +
  // doesn't leak DOM nodes, but Safari < 17 lacks support for some
  // formats — fall back to <img> on failure.
  let bitmap: ImageBitmap | HTMLImageElement
  try {
    bitmap = await loadImageBitmap(file)
  } catch {
    try {
      bitmap = await loadImageElement(file)
    } catch {
      return {
        ok: false,
        reason: "decode-failed",
        message: "Couldn't read the photo. Retake or pick a different one.",
      }
    }
  }

  const { width, height } = "naturalWidth" in bitmap
    ? { width: bitmap.naturalWidth, height: bitmap.naturalHeight }
    : { width: bitmap.width, height: bitmap.height }

  const longest = Math.max(width, height)
  const needsDownscale = longest > MAX_EDGE_PX || file.size > SOFT_MAX_BYTES
  if (!needsDownscale) {
    return {
      ok: true,
      file,
      downscaled: false,
      originalSize: file.size,
      wasSoftLarge: false,
    }
  }

  // Downscale — preserve aspect, max edge = MAX_EDGE_PX.
  const scale = longest > MAX_EDGE_PX ? MAX_EDGE_PX / longest : 1
  const targetW = Math.round(width * scale)
  const targetH = Math.round(height * scale)

  let blob: Blob
  try {
    blob = await drawAndExport(bitmap, targetW, targetH)
  } catch {
    return {
      ok: false,
      reason: "downscale-failed",
      message: "Couldn't process the photo. Try retaking it.",
    }
  } finally {
    if ("close" in bitmap) bitmap.close()
  }

  // Preserve the original filename so a thoughtful BE can tell.
  const newName = file.name.replace(/\.[^.]+$/, "") + "-q.jpg"
  const downscaled = new File([blob], newName, { type: "image/jpeg" })
  return {
    ok: true,
    file: downscaled,
    downscaled: true,
    originalSize: file.size,
    wasSoftLarge: file.size > SOFT_MAX_BYTES,
  }
}

async function loadImageBitmap(file: File): Promise<ImageBitmap> {
  if (typeof createImageBitmap !== "function") {
    throw new Error("createImageBitmap unavailable")
  }
  return createImageBitmap(file)
}

async function loadImageElement(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error("image-load-failed"))
    }
    img.src = url
  })
}

async function drawAndExport(
  source: ImageBitmap | HTMLImageElement,
  w: number,
  h: number,
): Promise<Blob> {
  const canvas = document.createElement("canvas")
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext("2d")
  if (!ctx) throw new Error("no-2d-context")
  ctx.drawImage(source, 0, 0, w, h)
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return reject(new Error("toBlob-null"))
        resolve(blob)
      },
      "image/jpeg",
      JPEG_QUALITY,
    )
  })
}
